import React, { useState, useCallback } from "react";
import Head from "next/head";
import Page from "@components/Layout/page";
import { ContentContainer } from "@components/ContentContainer/ContentContainer";
import { WEBPAGE_TITLE } from "@utils/Constants";
import styles from "./CarbonCalculatorPage.module.scss";
import { resolveApiBase } from "@utils/apiBase";

const API_BASE = resolveApiBase(
    process.env.LAUFID_ENDPOINT,
    "http://api.app.local",
);

async function apiLookup(vara, supplier) {
    const res = await fetch(`${API_BASE}/public/epd/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vara, supplier }),
    });
    const data = await res.json();
    return data.results || data;
}

async function apiSearchProducts(q, supplier) {
    let url = `${API_BASE}/public/epd/search-products?q=${encodeURIComponent(q)}&limit=10`;
    if (supplier) url += `&supplier=${encodeURIComponent(supplier)}`;
    const res = await fetch(url);
    const data = await res.json();
    return data.results || data;
}

async function apiCalculate(lines) {
    const res = await fetch(`${API_BASE}/public/epd/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines }),
    });
    const data = await res.json();
    return data.results || data;
}

export default function CarbonCalculatorPage() {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState(null);
    const [productResults, setProductResults] = useState(null);

    const [batchText, setBatchText] = useState("");
    const [batchResult, setBatchResult] = useState(null);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchError, setBatchError] = useState(null);

    const SUPPLIERS = [
        {
            id: "husasmidjan",
            label: "Húsasmiðjan",
            color: "#c41230",
            logo: "/img/husasmidjan-logo.png",
        },
        {
            id: "byko",
            label: "Byko",
            color: "#e8600a",
            logo: "/img/byko-logo.webp",
        },
        { id: "other", label: "Annar birgi", color: "#666", logo: null },
    ];

    const handleLookup = useCallback(async () => {
        if (!searchQuery.trim()) return;
        setLookupLoading(true);
        setLookupError(null);
        setLookupResult(null);
        setProductResults(null);
        try {
            const [lookupRes, productRes] = await Promise.all([
                apiLookup(searchQuery.trim(), selectedSupplier),
                apiSearchProducts(searchQuery.trim(), selectedSupplier),
            ]);
            setLookupResult(lookupRes);
            if (productRes.products && productRes.products.length > 0) {
                setProductResults(productRes);
            }
        } catch (err) {
            setLookupError("Villa kom upp við að leita að vöru. Reyndu aftur.");
        }
        setLookupLoading(false);
    }, [searchQuery, selectedSupplier]);

    const handleBatchCalculate = useCallback(async () => {
        if (!batchText.trim()) return;
        setBatchLoading(true);
        setBatchError(null);
        setBatchResult(null);
        try {
            const lines = batchText
                .trim()
                .split("\n")
                .map((line) => {
                    const parts = line.split(";").map((p) => p.trim());
                    return {
                        vara: parts[0] || "",
                        quantity: parts[1] || "1",
                        unit: parts[2] || "Stk",
                    };
                })
                .filter((l) => l.vara);
            const result = await apiCalculate(lines);
            setBatchResult(result);
        } catch (err) {
            setBatchError("Villa kom upp við útreikning. Reyndu aftur.");
        }
        setBatchLoading(false);
    }, [batchText]);

    const confidenceLabel = (c) => {
        switch (c) {
            case "high":
                return "Hátt";
            case "medium":
                return "Miðlungs";
            case "low":
                return "Lágt";
            default:
                return "Ekkert";
        }
    };

    return (
        <Page fluid>
            <Head>
                <title>Kolefnisreiknir | {WEBPAGE_TITLE}</title>
                <meta
                    name="description"
                    content="Reiknaðu kolefnisspor byggingarefna — sjálfkrafa úr innkaupagögnum."
                />
            </Head>

            <div className={styles.carbonPage}>
                <ContentContainer>
                    <div className={styles.heroSection}>
                        <div className={styles.heroContent}>
                            <h1 className={styles.heroTitle}>
                                Kolefnisreiknir
                            </h1>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "14px",
                                    marginBottom: "1rem",
                                }}
                            >
                                <img
                                    src="/img/laufid-logo-no-padding.svg"
                                    alt="Laufið"
                                    style={{ height: "24px" }}
                                />
                                <span
                                    style={{
                                        color: "#bbb",
                                        fontSize: "1.4rem",
                                        fontWeight: 300,
                                    }}
                                >
                                    +
                                </span>
                                <img
                                    src="/img/kolka-logo.png"
                                    alt="Kolka"
                                    style={{ height: "38px" }}
                                />
                            </div>
                            <p className={styles.heroSubtitle}>
                                Laufið treystir{" "}
                                <strong style={{ color: "#1a1a1a" }}>
                                    kolefnisreikni Kolku
                                </strong>{" "}
                                fyrir úrvinnslu byggingarefna. Sláðu inn
                                vöruheiti eða vörunúmer frá Húsasmiðjunni, Byko
                                eða öðrum birgjum og sjáðu kolefnisspor
                                efnisins, byggt á opinberum EPD gögnum.
                            </p>
                            <p
                                style={{
                                    fontSize: "0.85rem",
                                    color: "#888",
                                    marginBottom: "1.5rem",
                                }}
                            >
                                Ertu með vörunúmer af reikningi? Sláðu inn á
                                sniði: <strong>219682_Múrplata 600x1200</strong>
                            </p>

                            <div className={styles.supplierSelect}>
                                <p
                                    style={{
                                        fontSize: "0.9rem",
                                        color: "#555",
                                        marginBottom: "10px",
                                        fontWeight: 500,
                                        textAlign: "center",
                                    }}
                                >
                                    Hvaðan eru gögnin þín?
                                </p>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        flexWrap: "wrap",
                                        marginBottom: "1.5rem",
                                        justifyContent: "center",
                                    }}
                                >
                                    {SUPPLIERS.map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={() =>
                                                setSelectedSupplier(
                                                    selectedSupplier === s.id
                                                        ? null
                                                        : s.id,
                                                )
                                            }
                                            className={styles.supplierButton}
                                            style={{
                                                borderColor:
                                                    selectedSupplier === s.id
                                                        ? s.color
                                                        : "#ddd",
                                                background:
                                                    selectedSupplier === s.id
                                                        ? `${s.color}10`
                                                        : "#fff",
                                                color:
                                                    selectedSupplier === s.id
                                                        ? s.color
                                                        : "#555",
                                                fontWeight:
                                                    selectedSupplier === s.id
                                                        ? 600
                                                        : 400,
                                            }}
                                        >
                                            {s.logo ? (
                                                <img
                                                    src={s.logo}
                                                    alt={s.label}
                                                    style={{
                                                        height: "22px",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            ) : (
                                                s.label
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </ContentContainer>

                <ContentContainer>
                    <div className={styles.searchSection}>
                        <div className={styles.searchBox}>
                            <input
                                className={styles.searchInput}
                                type="text"
                                placeholder="t.d. steinull, gips, timbur, fermacell..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) =>
                                    e.key === "Enter" && handleLookup()
                                }
                            />
                            <button
                                className={styles.searchButton}
                                onClick={handleLookup}
                                disabled={lookupLoading || !searchQuery.trim()}
                            >
                                {lookupLoading ? "Leita..." : "Leita"}
                            </button>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                                marginBottom: "1.5rem",
                                justifyContent: "center",
                            }}
                        >
                            {[
                                "gips",
                                "timbur",
                                "fura",
                                "stál",
                                "einangrun",
                                "steypa",
                                "fermacell",
                                "saum",
                                "lím",
                                "MDF",
                            ].map((term) => (
                                <button
                                    key={term}
                                    onClick={() => {
                                        setSearchQuery(term);
                                    }}
                                    style={{
                                        padding: "6px 14px",
                                        fontSize: "0.85rem",
                                        background: "#f0f4f0",
                                        border: "1px solid #d4ddd4",
                                        borderRadius: "20px",
                                        cursor: "pointer",
                                        color: "#333",
                                        transition: "all 0.15s",
                                    }}
                                    onMouseOver={(e) =>
                                        (e.target.style.background = "#e0ece0")
                                    }
                                    onMouseOut={(e) =>
                                        (e.target.style.background = "#f0f4f0")
                                    }
                                >
                                    {term}
                                </button>
                            ))}
                        </div>

                        {lookupError && (
                            <div className={styles.error}>{lookupError}</div>
                        )}

                        {lookupResult && (
                            <LookupResultCard
                                result={lookupResult}
                                styles={styles}
                                confidenceLabel={confidenceLabel}
                            />
                        )}

                        {productResults &&
                            productResults.products &&
                            productResults.products.length > 0 && (
                                <ProductList
                                    results={productResults}
                                    styles={styles}
                                />
                            )}

                        <div className={styles.batchSection}>
                            <h2 className={styles.batchTitle}>
                                Reikna kolefnisspor margra vara
                            </h2>
                            <p className={styles.batchSubtitle}>
                                Límdu inn vörulínur — ein lína per vöru. Snið:{" "}
                                <code>Vara;Magn;Eining</code>
                            </p>
                            <textarea
                                className={styles.textarea}
                                placeholder={`130999_Spónaplata 120x252 Cm :12 Mm;5;Stk\n120115_Gipspl 120x255:13mm Gkb-1;20;Stk\n219682_Múrplata 600x1200;10;Stk`}
                                value={batchText}
                                onChange={(e) => setBatchText(e.target.value)}
                            />
                            <button
                                className={styles.searchButton}
                                onClick={handleBatchCalculate}
                                disabled={batchLoading || !batchText.trim()}
                            >
                                {batchLoading
                                    ? "Reikna..."
                                    : "Reikna kolefnisspor"}
                            </button>

                            {batchError && (
                                <div className={styles.error}>{batchError}</div>
                            )}

                            {batchResult && (
                                <BatchResults
                                    result={batchResult}
                                    styles={styles}
                                />
                            )}
                        </div>

                        <div
                            className={styles.ctaSection}
                            style={{ marginTop: "4rem" }}
                        >
                            <h3 className={styles.ctaTitle}>
                                Viltu sjá öll innkaupin þín?
                            </h3>
                            <p className={styles.ctaText}>
                                Tengdu Kolka-gögnin þín og fáðu kolefnisspor
                                allra innkaupa sjálfkrafa — tilbúið fyrir
                                lífsferilsgreiningu.
                            </p>
                            <a
                                href="https://app.laufid.is"
                                className={styles.ctaButton}
                            >
                                Skoða Laufið
                            </a>
                        </div>
                    </div>
                </ContentContainer>
            </div>
        </Page>
    );
}

function VerificationBadge({ epd }) {
    const status = epd.verification_status || "estimated";
    const dataSource = epd.data_source;

    const config = {
        verified: {
            bg: "#f0fdf4",
            border: "#bbf7d0",
            icon: "✓",
            iconColor: "#16a34a",
            title: "Staðfest gögn",
            text: "Gildin eru lesin beint úr opinberu EPD-skjali framleiðanda.",
            textColor: "#166534",
        },
        estimated: {
            bg: "#fefce8",
            border: "#fde68a",
            icon: "≈",
            iconColor: "#ca8a04",
            title: "Áætluð gildi",
            text: "Gildin eru metin út frá sambærilegum vörum. Ekki enn sannreynd úr PDF.",
            textColor: "#92400e",
        },
        generic: {
            bg: "#f9fafb",
            border: "#e5e7eb",
            icon: "⋯",
            iconColor: "#6b7280",
            title: "Almenn iðnaðargildi",
            text: "Meðaltalsgildi úr opinberum gagnagrunnum (Boverket, ÖKOBAUDAT, CO2data.fi).",
            textColor: "#4b5563",
        },
        expired: {
            bg: "#fef2f2",
            border: "#fecaca",
            icon: "⏱",
            iconColor: "#dc2626",
            title: "EPD er útrunnið",
            text: "Gildin eru úr EPD sem hefur runnið út. Þörf er á uppfærðri útgáfu fyrir skilagátt.",
            textColor: "#991b1b",
        },
    };

    const cfg = config[status] || config.estimated;

    return (
        <div
            style={{
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                padding: "10px 14px",
                borderRadius: "8px",
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
            }}
        >
            <span
                style={{
                    fontSize: "1.1rem",
                    color: cfg.iconColor,
                    fontWeight: 700,
                    marginTop: "-1px",
                }}
            >
                {cfg.icon}
            </span>
            <div style={{ flex: 1 }}>
                <strong
                    style={{
                        color: cfg.textColor,
                        display: "block",
                        fontSize: "0.9rem",
                        marginBottom: "2px",
                    }}
                >
                    {cfg.title}
                    {dataSource && (
                        <span
                            style={{
                                fontWeight: 400,
                                opacity: 0.75,
                                marginLeft: "8px",
                            }}
                        >
                            · {dataSource}
                        </span>
                    )}
                </strong>
                <span
                    style={{
                        color: cfg.textColor,
                        fontSize: "0.82rem",
                        opacity: 0.9,
                    }}
                >
                    {cfg.text}
                </span>
            </div>
        </div>
    );
}

function LookupResultCard({ result, styles, confidenceLabel }) {
    const epd = result.epd;
    const matchType = result.match_type;
    const parsed = result.parsed;
    const category = result.category;

    if (matchType === "none" || !epd) {
        const isJustNumber = /^\d+$/.test(parsed?.raw?.trim());
        return (
            <div className={styles.resultCard}>
                <div className={styles.resultHeader}>
                    <span className={styles.productName}>
                        {parsed?.product_name || "Óþekkt vara"}
                    </span>
                    <span className={`${styles.matchBadge} ${styles.none}`}>
                        Ekkert EPD fundið
                    </span>
                </div>
                <p style={{ color: "#888", fontSize: "0.9rem" }}>
                    {isJustNumber
                        ? 'Vörunúmer eitt og sér dugar ekki — sláðu inn efnisheiti eins og „steinull", „gipsplata", eða Kolka-snið: „130999_Spónaplata 120x252 Cm :12 Mm".'
                        : 'Engin EPD pörun fannst. Prófaðu efnisheiti eins og „steinull", „gips", „timbur" eða „fermacell".'}
                </p>
            </div>
        );
    }

    const confidence =
        matchType === "supplier_product"
            ? "high"
            : epd.epd_type === "product"
              ? "high"
              : epd.epd_type === "industry"
                ? "medium"
                : "low";

    return (
        <div className={styles.resultCard}>
            <div className={styles.resultHeader}>
                <span className={styles.productName}>
                    {parsed?.product_name || epd.product_name}
                </span>
                <span className={`${styles.matchBadge} ${styles[confidence]}`}>
                    {confidenceLabel(confidence)}
                </span>
            </div>

            <VerificationBadge epd={epd} />

            {epd.is_expired && (
                <div
                    style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                    }}
                >
                    <span style={{ fontSize: "1.2rem", marginTop: "-2px" }}>
                        ⚠️
                    </span>
                    <div>
                        <strong
                            style={{
                                color: "#991b1b",
                                display: "block",
                                fontSize: "0.95rem",
                                marginBottom: "4px",
                            }}
                        >
                            Viðvörun: EPD skjal er útrunnið
                        </strong>
                        <span style={{ color: "#b91c1c", fontSize: "0.85rem" }}>
                            Gildistími þessa EPD skjals er runninn út. Mælt er
                            með að leita að nýrri útgáfu til að tryggja að
                            útreikningur stenst formlegar kröfur HMS um
                            lífsferilsgreiningar.
                        </span>
                    </div>
                </div>
            )}
            {epd.expires_soon && (
                <div
                    style={{
                        background: "#fffbeb",
                        border: "1px solid #fef08a",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                    }}
                >
                    <span style={{ fontSize: "1.2rem", marginTop: "-2px" }}>
                        ⏱️
                    </span>
                    <div>
                        <strong
                            style={{
                                color: "#854d0e",
                                display: "block",
                                fontSize: "0.95rem",
                                marginBottom: "4px",
                            }}
                        >
                            Athugið: EPD skjal rennur út bráðum
                        </strong>
                        <span style={{ color: "#a16207", fontSize: "0.85rem" }}>
                            Þetta skjal rennur út innan 90 daga. Hafið í huga að
                            uppfærð gildi gætu tekið gildi fljótlega.
                        </span>
                    </div>
                </div>
            )}

            <div className={styles.gwpGrid}>
                <div className={styles.gwpCard}>
                    <div className={styles.gwpValue}>
                        {epd.gwp_a1a3_fossil
                            ? Number(epd.gwp_a1a3_fossil).toFixed(2)
                            : "—"}
                    </div>
                    <div className={styles.gwpLabel}>
                        kg CO₂e / {epd.declared_unit}
                        <br />
                        A1–A3 (framleiðsla)
                    </div>
                </div>
                <div className={styles.gwpCard}>
                    <div className={styles.gwpValue}>
                        {epd.gwp_a4 ? Number(epd.gwp_a4).toFixed(3) : "—"}
                    </div>
                    <div className={styles.gwpLabel}>
                        kg CO₂e / {epd.declared_unit}
                        <br />
                        A4 (flutningur)
                    </div>
                </div>
                <div className={styles.gwpCard}>
                    <div className={styles.gwpValue}>
                        {epd.gwp_c1c4 ? Number(epd.gwp_c1c4).toFixed(3) : "—"}
                    </div>
                    <div className={styles.gwpLabel}>
                        kg CO₂e / {epd.declared_unit}
                        <br />
                        C1–C4 (förgun)
                    </div>
                </div>
            </div>

            <div className={styles.epdInfo}>
                <span>EPD: {epd.epd_number}</span>
                <span>Framleiðandi: {epd.manufacturer}</span>
                {category && <span>Flokkur: {category.category_label}</span>}
                <span>
                    Tegund:{" "}
                    {epd.epd_type === "product"
                        ? "Vörusérstakt"
                        : epd.epd_type === "industry"
                          ? "Branche"
                          : "Generískt"}
                </span>
                {epd.source_url && (
                    <span>
                        Heimild:{" "}
                        <a
                            href={epd.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#009448", textDecoration: "none" }}
                        >
                            Skoða EPD skjal ↗
                        </a>
                    </span>
                )}
            </div>
        </div>
    );
}

function ProductList({ results, styles }) {
    const { products, total, query } = results;

    const supplierLogos = {
        Húsasmiðjan: "/img/husasmidjan-logo.png",
        Byko: "/img/byko-logo.webp",
    };

    const supplierColors = {
        Húsasmiðjan: "#c41230",
        Byko: "#e8600a",
    };

    return (
        <div className={styles.resultCard} style={{ marginTop: "1rem" }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "14px",
                }}
            >
                <span style={{ fontWeight: 600, color: "#111" }}>
                    Vörur sem passa við „{query}“
                </span>
                <span style={{ fontSize: "0.82rem", color: "#888" }}>
                    {total > 10
                        ? `Sýni 10 af ${total}`
                        : `${products.length} vörur`}
                </span>
            </div>

            <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
                {products.map((p, i) => (
                    <div
                        key={i}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "10px 14px",
                            background: "#fff",
                            border: "1px solid #eee",
                            borderRadius: "8px",
                            fontSize: "0.9rem",
                        }}
                    >
                        {supplierLogos[p.supplier_name] ? (
                            <img
                                src={supplierLogos[p.supplier_name]}
                                alt={p.supplier_name}
                                style={{
                                    height: "20px",
                                    width: "80px",
                                    objectFit: "contain",
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <span
                                style={{
                                    padding: "3px 8px",
                                    borderRadius: "4px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#fff",
                                    whiteSpace: "nowrap",
                                    background:
                                        supplierColors[p.supplier_name] ||
                                        "#888",
                                    minWidth: "80px",
                                    textAlign: "center",
                                }}
                            >
                                {p.supplier_name}
                            </span>
                        )}

                        <span style={{ flex: 1, color: "#333" }}>
                            <strong>{p.product_code}</strong> {p.product_name}
                        </span>

                        {p.gwp_a1a3_fossil && (
                            <span
                                style={{
                                    fontWeight: 700,
                                    color: "#009448",
                                    fontSize: "0.85rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {Number(p.gwp_a1a3_fossil).toFixed(2)} CO₂e/
                                {p.declared_unit}
                            </span>
                        )}

                        {p.supplier_url && (
                            <a
                                href={p.supplier_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    padding: "4px 10px",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    border: `1px solid ${supplierColors[p.supplier_name] || "#888"}`,
                                    borderRadius: "6px",
                                    textDecoration: "none",
                                    whiteSpace: "nowrap",
                                    color:
                                        supplierColors[p.supplier_name] ||
                                        "#888",
                                }}
                            >
                                Sjá vöru →
                            </a>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function BatchResults({ result, styles }) {
    const { totals, by_category, lines } = result;

    return (
        <div className={styles.batchResults}>
            <div className={styles.totalsGrid}>
                <div className={styles.totalCard}>
                    <div className={styles.totalValue}>
                        {totals.gwp_lifecycle_kg_co2e
                            ? Number(
                                  totals.gwp_lifecycle_kg_co2e,
                              ).toLocaleString("is-IS", {
                                  maximumFractionDigits: 0,
                              })
                            : "0"}
                    </div>
                    <div className={styles.totalLabel}>
                        kg CO₂e (Heildar LCA spor A-C)
                    </div>
                </div>
                <div className={styles.totalCard}>
                    <div className={styles.totalValue}>
                        {totals.gwp_lifecycle_tonnes_co2e || "0"}
                    </div>
                    <div className={styles.totalLabel}>
                        tonn CO₂e (Heildar LCA spor A-C)
                    </div>
                </div>
                <div className={styles.totalCard}>
                    <div className={styles.totalValue}>
                        {totals.match_rate_pct || 0}%
                    </div>
                    <div className={styles.totalLabel}>pörunarhlutfall</div>
                </div>
                <div className={styles.totalCard}>
                    <div className={styles.totalValue}>
                        {totals.lines_matched || 0} / {totals.lines_total || 0}
                    </div>
                    <div className={styles.totalLabel}>línur paraðar</div>
                </div>
            </div>

            {by_category && Object.keys(by_category).length > 0 && (
                <>
                    <h3 style={{ marginBottom: "0.8rem", color: "#111" }}>
                        Sundurliðun eftir efnistegund
                    </h3>
                    <table className={styles.categoryTable}>
                        <thead>
                            <tr>
                                <th>Efnistegund</th>
                                <th>Línur</th>
                                <th>kg CO₂e (Heildar LCA)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(by_category)
                                .sort(
                                    ([, a], [, b]) =>
                                        b.gwp_lifecycle - a.gwp_lifecycle,
                                )
                                .map(([cat, data]) => (
                                    <tr key={cat}>
                                        <td>{cat}</td>
                                        <td>{data.lines}</td>
                                        <td>
                                            {Number(
                                                data.gwp_lifecycle,
                                            ).toLocaleString("is-IS", {
                                                maximumFractionDigits: 0,
                                            })}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}
